import { useEffect, useState } from 'react';
import { parkingQueueApi } from '../../api/parkingQueueApi';
import { leasesApi } from '../../api/leasesApi';
import { getParkingQueueConfig } from '../../api/parkingQueueConfigApi';
import { QueueRowFacts, rowFacts, spotCandidates, SpotRef } from '../../Helpers/parkingQueueUtils';
import { todayStockholm } from '../../Helpers/leaseUtils';
import { DEFAULT_PARKING_QUEUE_CONFIG, ParkingQueueConfig } from '../../types/parkingQueueConfig';

export interface SpotQueueCandidates {
    /** Active entries the spot matches, in the standard order; the first is first in line. */
    rows: QueueRowFacts[];
    config: ParkingQueueConfig;
    loading: boolean;
    error: string | null;
}

/**
 * Entries, every lease (spots held and residence are instance-wide) and the
 * order config. Lighter than `useParkingQueue`, which also loads every
 * building's areas and units. Admin-only root.
 */
const loadSpotCandidates = async (ref: SpotRef): Promise<{ rows: QueueRowFacts[]; config: ParkingQueueConfig }> => {
    const [entries, leases, config] = await Promise.all([parkingQueueApi.getAll(), leasesApi.getAll(), getParkingQueueConfig()]);
    const today = todayStockholm();
    return { rows: spotCandidates(entries.map(e => rowFacts(e, leases, today)), ref, config), config };
};

/** Who in the parking queue a spot can go to, read when mounted and again when `reloadKey` changes. */
export const useSpotQueueCandidates = (ref: SpotRef, reloadKey = 0): SpotQueueCandidates => {
    const [state, setState] = useState<SpotQueueCandidates>({ rows: [], config: DEFAULT_PARKING_QUEUE_CONFIG, loading: true, error: null });
    const { buildingKey, areaKey, spotKey } = ref;

    useEffect(() => {
        let cancelled = false;
        setState(s => ({ ...s, loading: true, error: null }));
        loadSpotCandidates(ref)
            .then(({ rows, config }) => {
                if (!cancelled) setState({ rows, config, loading: false, error: null });
            })
            .catch(e => {
                console.error(e);
                if (!cancelled) setState(s => ({ ...s, loading: false, error: 'Kunde inte läsa parkeringskön' }));
            });
        return () => { cancelled = true; };
        // Read once per spot and reload; the spot's wish-relevant fields come with it.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [buildingKey, areaKey, spotKey, reloadKey]);

    return state;
};

export interface SpotQueueCheck {
    checking: boolean;
    /** How many in the queue the spot matches, read now; 0 when the queue cannot be read (it never blocks a lease). */
    count: (ref: SpotRef) => Promise<number>;
}

/** One-off read of a spot's queue candidates, e.g. before Nytt hyresavtal. */
export const useSpotQueueCheck = (): SpotQueueCheck => {
    const [checking, setChecking] = useState(false);
    const count = async (ref: SpotRef) => {
        setChecking(true);
        try {
            return (await loadSpotCandidates(ref)).rows.length;
        } catch (e) {
            console.error(e);
            return 0;
        } finally {
            setChecking(false);
        }
    };
    return { checking, count };
};
